import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { corporateInput } from "@/components/Shared";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Key, ShieldCheck, Clock, EnvelopeSimple, LockKey, Trash, Warning, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Security page — unified place for security-sensitive user actions.
 * Currently exposes:
 *   - Change password (active)
 *   - Session info (read-only summary)
 * Future-proof container for:
 *   - 2FA enrollment
 *   - API tokens
 *   - Audit log of own actions
 *   - Email verification
 */
export default function SecurityPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const submitPassword = async () => {
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error(t("pwd.mismatch")); return;
    }
    if (pwdForm.new_password.length < 8) {
      toast.error(t("pwd.tooShort")); return;
    }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success(t("pwd.success"));
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPwdSaving(false); }
  };

  return (
    <PageShell
      section={lang === "en" ? "System" : "Sistema"}
      page={t("nav.security", "Seguridad")}
      context={user?.email}
      testid="security-toolbar"
    >
      <div className="mb-6 border-l-2 border-amber-400 pl-4 py-2">
        <div className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <ShieldCheck size={16} className="text-amber-500" />
          {lang === "en" ? "Account security" : "Seguridad de la cuenta"}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {lang === "en"
            ? "Manage your password and security settings. More options coming soon (2FA, API keys, audit log)."
            : "Gestiona tu contraseña y opciones de seguridad. Próximamente: autenticación de dos factores, tokens API, registro de auditoría."}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Change password card */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
            <Key size={15} className="text-slate-500" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{lang === "en" ? "Credentials" : "Credenciales"}</div>
              <div className="text-sm font-semibold text-slate-900 dark:text-white">{t("pwd.title")}</div>
            </div>
          </div>
          <div className="p-5 space-y-3 max-w-md">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.current")} *</Label>
              <PasswordInput autoComplete="current-password" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} className={corporateInput} data-testid="sec-pwd-current" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.new")} *</Label>
              <PasswordInput autoComplete="new-password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} className={corporateInput} data-testid="sec-pwd-new" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.confirm")} *</Label>
              <PasswordInput autoComplete="new-password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} className={corporateInput} data-testid="sec-pwd-confirm" />
            </div>
            <p className="text-[10px] text-slate-500">{t("pwd.hint")}</p>
            <Button
              onClick={submitPassword}
              disabled={pwdSaving || !pwdForm.current_password || !pwdForm.new_password}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="sec-pwd-submit"
            >
              {pwdSaving ? t("common.saving") : t("pwd.submit")}
            </Button>
          </div>
        </div>

        {/* Account info */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
            <ShieldCheck size={15} className="text-slate-500" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{lang === "en" ? "Account" : "Cuenta"}</div>
              <div className="text-sm font-semibold text-slate-900 dark:text-white">{lang === "en" ? "Session info" : "Información de sesión"}</div>
            </div>
          </div>
          <div className="p-5 space-y-3 text-xs">
            <Row icon={EnvelopeSimple} label="Email" value={user?.email} />
            <Row icon={ShieldCheck} label={lang === "en" ? "Role" : "Rol"} value={user?.role} mono />
            <Row icon={Clock} label={lang === "en" ? "Logged in" : "Conectado"} value={new Date().toLocaleString(lang === "en" ? "en-US" : "es-MX")} mono />
          </div>
        </div>

        {/* Coming soon — 2FA placeholder */}
        <div className="lg:col-span-3 bg-slate-50 dark:bg-slate-800/40 border border-dashed border-slate-300 dark:border-slate-700 rounded-md p-5">
          <div className="flex items-start gap-3">
            <LockKey size={20} className="text-slate-400 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {lang === "en" ? "Two-factor authentication (2FA)" : "Autenticación de dos factores (2FA)"}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
                {lang === "en"
                  ? "Add an extra layer of security with TOTP (Google Authenticator, Authy, 1Password). Coming soon — we'll notify you when available."
                  : "Añade una capa extra de seguridad con TOTP (Google Authenticator, Authy, 1Password). Próximamente — te avisaremos cuando esté disponible."}
              </div>
            </div>
            <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-400 px-2 py-0.5 border border-amber-300 dark:border-amber-700 rounded">
              {lang === "en" ? "Coming soon" : "Próximamente"}
            </span>
          </div>

          {/* Square credentials — admin only */}
          {user?.role === "admin" && (
            <SquareCredentialsCard lang={lang} />
          )}

          {/* Factory reset — DESTRUCTIVE */}
          {user?.role === "admin" && (
            <FactoryResetCard lang={lang} />
          )}
        </div>
      </div>
    </PageShell>
  );
}

function FactoryResetCard({ lang }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: true, performed: false, at: null, by: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/factory-reset/status");
        if (!cancelled) setStatus({ loading: false, ...data });
      } catch {
        if (!cancelled) setStatus({ loading: false, performed: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const run = async (dryRun) => {
    if (!token) { toast.error(lang === "en" ? "Token required" : "Token requerido"); return; }
    if (!dryRun && !confirm(lang === "en"
      ? "ATENCIÓN — This will PERMANENTLY DELETE all clients, invoices, threads, leads, etc. Admin users and plans are preserved. This action can ONLY be executed ONCE. Are you absolutely sure?"
      : "ATENCIÓN — Esto BORRARÁ PERMANENTEMENTE todos los clientes, facturas, threads, leads, etc. Admins y planes se conservan. Esta acción SOLO puede ejecutarse UNA VEZ. ¿Estás absolutamente seguro?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/factory-reset?token=${encodeURIComponent(token)}&dry_run=${dryRun}`);
      setResult(data);
      toast.success(dryRun
        ? (lang === "en" ? "Dry-run complete" : "Simulación completa")
        : (lang === "en" ? "Database reset complete" : "Base de datos limpiada"));
      if (!dryRun) {
        // Refresh status so the card collapses on next render
        setStatus({ loading: false, performed: true, at: new Date().toISOString(), by: "you" });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  // Hide once already performed
  if (status.loading) return null;
  if (status.performed) {
    return (
      <div className="mt-6 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 rounded-md p-4" data-testid="factory-reset-done">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {lang === "en" ? "Factory reset already executed" : "Reinicio total ya ejecutado"}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {lang === "en" ? "Performed on" : "Ejecutado el"}{" "}
              <span className="font-mono">{status.at ? new Date(status.at).toLocaleString(lang === "en" ? "en-US" : "es-MX") : "—"}</span>
              {status.by ? <> {lang === "en" ? "by" : "por"} <span className="font-mono">{status.by}</span></> : null}
              {". "}{lang === "en" ? "This control is disabled (one-time use)." : "Este control está deshabilitado (uso único)."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/20 rounded-md p-4" data-testid="factory-reset-card">
      <div className="flex items-start gap-3">
        <Warning size={18} weight="fill" className="text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-rose-900 dark:text-rose-200">
            {lang === "en" ? "Factory reset — DESTRUCTIVE" : "Reinicio total — DESTRUCTIVO"}
          </div>
          <p className="text-xs text-rose-800 dark:text-rose-300 mt-1 max-w-2xl">
            {lang === "en"
              ? "Wipe all business data (clients, invoices, threads, leads, activities) before going live with real Square data. Admin users and plans are preserved."
              : "Borra todos los datos de negocio (clientes, facturas, threads, leads, actividades) antes de salir en vivo con tu data real de Square. Admins y planes se preservan."}
          </p>
          {!open && (
            <button
              onClick={() => setOpen(true)}
              data-testid="factory-reset-toggle"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-700 hover:bg-rose-100 dark:hover:bg-rose-900/40 rounded transition-colors"
            >
              <Trash size={12} /> {lang === "en" ? "Show controls" : "Mostrar controles"}
            </button>
          )}
          {open && (
            <div className="mt-3 space-y-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-rose-800 dark:text-rose-300 font-semibold">
                  {lang === "en" ? "Reset token (from server env)" : "Token de reinicio (env del servidor)"}
                </Label>
                <Input value={token} onChange={(e) => setToken(e.target.value)} type="password" placeholder="FACTORY_RESET_TOKEN" className={corporateInput} data-testid="factory-reset-token" />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => run(true)} disabled={busy} className="h-8 bg-slate-700 hover:bg-slate-800 text-white text-xs" data-testid="factory-reset-dryrun">
                  {lang === "en" ? "Dry-run (preview only)" : "Simular (solo vista previa)"}
                </Button>
                <Button onClick={() => run(false)} disabled={busy} className="h-8 bg-rose-600 hover:bg-rose-700 text-white text-xs" data-testid="factory-reset-execute">
                  <Trash size={12} className="mr-1" /> {lang === "en" ? "Wipe data" : "Borrar datos"}
                </Button>
              </div>
              {result && (
                <pre className="mt-2 p-2 bg-slate-900 text-emerald-300 text-[10px] rounded overflow-x-auto max-h-48" data-testid="factory-reset-result">
                  {JSON.stringify(result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={13} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
        <div className={`text-slate-900 dark:text-slate-100 truncate mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
      </div>
    </div>
  );
}


function SquareCredentialsCard({ lang }) {
  const [status, setStatus] = useState({ loading: true, configured: false });
  const [form, setForm] = useState({
    access_token: "",
    location_id: "",
    environment: "production",
    webhook_signature_key: "",
  });
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/square-credentials");
      setStatus({ loading: false, ...data });
    } catch {
      setStatus({ loading: false, configured: false });
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/square-credentials");
        if (!cancelled) setStatus({ loading: false, ...data });
      } catch {
        if (!cancelled) setStatus({ loading: false, configured: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    if (!form.access_token || !form.location_id) {
      toast.error(lang === "en" ? "Token and Location ID required" : "Token y Location ID son obligatorios");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/square-credentials", form);
      toast.success(lang === "en" ? "Credentials saved" : "Credenciales guardadas");
      setForm({ ...form, access_token: "", webhook_signature_key: "" });
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/square-credentials/test");
      setTestResult({ ok: true, ...data });
      toast.success(lang === "en" ? "Connection OK" : "Conexión OK");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Error";
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const clearCreds = async () => {
    if (!confirm(lang === "en"
      ? "Remove DB credentials and fall back to env vars?"
      : "¿Borrar credenciales de la DB y volver a usar variables de entorno?")) return;
    setBusy(true);
    try {
      await api.delete("/admin/square-credentials");
      toast.success(lang === "en" ? "Credentials removed" : "Credenciales eliminadas");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  if (status.loading) return null;

  return (
    <div className="mt-6 border border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/10 rounded-md p-4" data-testid="square-creds-card">
      <div className="flex items-start gap-3">
        <Lightning size={18} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            {lang === "en" ? "Square API credentials" : "Credenciales API de Square"}
          </div>
          <p className="text-xs text-amber-800 dark:text-amber-300 mt-1 max-w-2xl">
            {lang === "en"
              ? "Connect your real (production) Square account so customer + payment sync pulls real data. Get the access token from Square Developer Dashboard → your app → Production Credentials."
              : "Conecta tu cuenta de Square real (producción) para que el sync importe tus clientes y pagos verdaderos. Obtén el access token en Square Developer Dashboard → tu app → Production Credentials."}
          </p>

          {/* Current status */}
          <div className="mt-3 p-2 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</div>
                {status.configured ? (
                  <div className="font-mono text-emerald-700 dark:text-emerald-400">
                    ● {status.environment === "production" ? "PRODUCTION (real)" : "SANDBOX (test)"}
                  </div>
                ) : (
                  <div className="font-mono text-rose-600 dark:text-rose-400">
                    ● Usando env vars (probablemente sandbox)
                  </div>
                )}
              </div>
              {status.configured && (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Location ID</div>
                    <div className="font-mono text-slate-900 dark:text-slate-100 truncate">{status.location_id || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Token</div>
                    <div className="font-mono text-slate-900 dark:text-slate-100">{status.access_token_masked || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Actualizado</div>
                    <div className="text-slate-700 dark:text-slate-300">{status.updated_at ? new Date(status.updated_at).toLocaleString(lang === "en" ? "en-US" : "es-MX") : "—"}</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="mt-3 space-y-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {lang === "en" ? "Access Token (Production)" : "Access Token (Producción)"} *
              </Label>
              <PasswordInput
                value={form.access_token}
                onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                placeholder="EAAA... (production token from Square Developer Dashboard)"
                className={corporateInput}
                data-testid="square-creds-token"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Location ID *</Label>
                <Input
                  value={form.location_id}
                  onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                  placeholder="L1234ABCD..."
                  className={corporateInput}
                  data-testid="square-creds-location"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{lang === "en" ? "Environment" : "Entorno"}</Label>
                <select
                  value={form.environment}
                  onChange={(e) => setForm({ ...form, environment: e.target.value })}
                  className="w-full h-9 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm"
                  data-testid="square-creds-env"
                >
                  <option value="production">production</option>
                  <option value="sandbox">sandbox</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Webhook Signature Key ({lang === "en" ? "optional" : "opcional"})
              </Label>
              <PasswordInput
                value={form.webhook_signature_key}
                onChange={(e) => setForm({ ...form, webhook_signature_key: e.target.value })}
                placeholder={lang === "en" ? "If you configured webhooks" : "Si configuraste webhooks"}
                className={corporateInput}
              />
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={save} disabled={busy} className="h-8 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="square-creds-save">
                {lang === "en" ? "Save credentials" : "Guardar credenciales"}
              </Button>
              <Button onClick={testConnection} disabled={busy} className="h-8 bg-slate-700 hover:bg-slate-800 text-white text-xs" data-testid="square-creds-test">
                {lang === "en" ? "Test connection" : "Probar conexión"}
              </Button>
              {status.configured && (
                <Button onClick={clearCreds} disabled={busy} className="h-8 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs">
                  {lang === "en" ? "Clear (revert to env)" : "Borrar (volver a env)"}
                </Button>
              )}
            </div>

            {testResult && (
              <div className={`mt-2 p-2 rounded text-[11px] ${testResult.ok ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800" : "bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-300 border border-rose-200 dark:border-rose-800"}`}>
                {testResult.ok
                  ? `✓ ${lang === "en" ? "OK" : "OK"} — ${testResult.sample_customers_returned} ${lang === "en" ? "customer(s) returned in ping" : "cliente(s) en ping"}${testResult.has_more ? " (+más)" : ""}`
                  : `✗ ${testResult.error}`}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
